import { KnownGroupType, KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { useQueryListGroups } from '../../../groups/groups.queries';
import { useInfiniteQueryCouriersList } from '../../couriers.queries';
import { CouriersListControlsState, CouriersListQueryInput } from '../logic/couriers-list-query.map';
import { CourierListActions } from '../actions/courier-list.actions';

// Default to every status the API returns rather than active only — a courier awaiting
// approval sits at 'pending', and hiding those hides the self-registration queue.
const initialControlsState: CouriersListControlsState = {
  sortBy: 'firstName',
  sortOrder: KnownSortOrder.Asc,
  searchTerm: '',
  status: 'all',
  groupID: ''
};

const searchParamOptions: UseSearchParamsOptions<CouriersListControlsState> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  },
  config: {
    debouncedValues: ['searchTerm']
  }
};

export function useCouriersList(
  projectID: string,
  fixedQuery?: CouriersListQueryInput,
  options?: {
    /** Load the carrier filter's options when it opens, or straight away if a carrier is already selected. */
    loadCarriersOnOpen?: boolean;
  }
) {
  const project = useQueryGetProject(projectID);
  const [formData, queryData, setFormData] = useSearchParams(initialControlsState, searchParamOptions);
  const [currentAction, setCurrentAction] = useState<CourierListActions | null>(null);
  const setAction = useCallback((action: CourierListActions | null) => {
    setCurrentAction(action);
  }, []);
  const result = useInfiniteQueryCouriersList(projectID, { ...queryData, ...fixedQuery });

  // A courier's carrier is the group they belong to, so the carrier filter is a search over
  // groups scoped to groupType: 'carriers', across this project and its children.
  const [carriersRequested, setCarriersRequested] = useState(!options?.loadCarriersOnOpen);
  const requestCarriers = useCallback(() => setCarriersRequested(true), []);
  const carrierGroups = useQueryListGroups(projectID, false, true, true, [KnownGroupType.Carriers], {
    enabled: carriersRequested || !!formData.groupID
  });
  const carrierOptions = useMemo(
    () => (carrierGroups.data || []).map((group) => ({ label: group.name, value: group.id })),
    [carrierGroups.data]
  );

  // If the selected carrier drops out of scope (e.g. the project changes), clear it rather
  // than silently filtering on a groupID that no longer resolves to anything. Waits for a
  // successful load, as a query that hasn't run yet has no options to check against.
  useEffect(() => {
    if (!carrierGroups.isSuccess || !formData.groupID) {
      return;
    }
    if (!carrierOptions.find((o) => o.value === formData.groupID)) {
      setFormData({ groupID: '' });
    }
  }, [carrierGroups.isSuccess, formData.groupID, carrierOptions, setFormData]);

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: result.hasNextPage || false,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    onScrollToBottom() {
      result.fetchNextPage();
    }
  };

  return {
    project,
    result,
    infiniteScroll,
    formData,
    setFormData,
    carrierOptions,
    carrierFilter: { options: carrierOptions, loading: carrierGroups.isLoading, onOpen: requestCarriers },
    actions: { currentAction, setAction }
  } as const;
}
